import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useToast } from './ToastContext';

export interface ConfirmActionOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  action: () => void | Promise<void>;
  successTitle?: string;
  successMessage?: string;
  errorTitle?: string;
}
type Pending = ConfirmActionOptions & { resolve: (confirmed: boolean) => void };
const Context = createContext<((options: ConfirmActionOptions) => Promise<boolean>) | null>(null);
export function useConfirmation() {
  const confirmAction = useContext(Context);
  if (!confirmAction) throw new Error('useConfirmation requires ConfirmationProvider.');
  return { confirmAction };
}
export function ConfirmationProvider({ children }: { children: React.ReactNode }) {
  const { addToast } = useToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const current = useRef<Pending | null>(null);
  const running = useRef(false);
  const mounted = useRef(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; current.current?.resolve(false); current.current = null; }; }, []);
  const confirmAction = useCallback((options: ConfirmActionOptions) => {
    // Ignore duplicate triggers instead of replacing a decision already on screen.
    if (current.current) return Promise.resolve(false);
    return new Promise<boolean>(resolve => { const next = { ...options, resolve }; current.current = next; setError(''); setPending(next); });
  }, []);
  const cancel = () => { if (running.current) return; current.current?.resolve(false); current.current = null; setPending(null); setError(''); };
  const submit = async () => {
    const request = current.current;
    if (!request || running.current) return;
    running.current = true; setBusy(true); setError('');
    try {
      await request.action();
      if (!mounted.current) return;
      current.current = null; setPending(null); request.resolve(true);
      if (request.successTitle) addToast('success', request.successTitle, request.successMessage);
    } catch (err: unknown) {
      if (!mounted.current) return;
      const message = err instanceof Error ? err.message : 'The action could not be completed. Please try again.';
      setError(message);
      addToast('error', request.errorTitle || 'Action not completed', message);
    } finally { running.current = false; if (mounted.current) setBusy(false); }
  };
  return <Context.Provider value={confirmAction}>{children}<ConfirmDialog open={!!pending} title={pending?.title || ''} description={pending?.description || ''} confirmLabel={pending?.confirmLabel} pendingLabel={pending?.pendingLabel} cancelLabel={pending?.cancelLabel} busy={busy} error={error} onCancel={cancel} onConfirm={submit}/></Context.Provider>;
}
